import { Spinner } from "@heroui/react";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "../../utils/toast";

import { getPost } from "../../services/api";
import {
    FileMetadata,
    PostResponseDto,
} from "../../types/types";
import { formatDateTime } from "../../utils/common/formatUtils";

const Post: React.FC = () => {
    const { postId: postIdParam } = useParams<{ postId: string }>();
    const navigate = useNavigate();
    const postId = Number(postIdParam);
    const { t } = useTranslation("feeds");
    const loginResponse = JSON.parse(localStorage.getItem("loginResponse") || "{}");
    const currentUserId = loginResponse.id;

    const [post, setPost] = useState<PostResponseDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const fetchPost = useCallback(async () => {
        if (isNaN(postId)) {
            setError(t("invalidPostId", "Invalid post ID"));
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const foundPost = await getPost(postId);
            if (!foundPost) {
                throw new Error(t("postNotFound", "Post not found"));
            }
            setPost(foundPost);
        } catch (err: any) {
            setError(t("failedToLoadPost", "Failed to load post") + ": " + (err.message || err));
            toast.error(t("failedFetchPosts", "Failed to load post"));
        } finally {
            setLoading(false);
        }
    }, [postId, currentUserId, t]);

    useEffect(() => {
        fetchPost();
    }, [fetchPost]);

    const renderMedia = (file: FileMetadata | undefined) => {
        if (!file?.fileUrl) return null;
        const isVideo = file.fileName?.match(/\.(mp4|webm|ogg|mov)$/i);
        const imageFrameClass = "w-full overflow-hidden bg-gray-100";
        const videoFrameClass =
            "w-full h-[300px] sm:h-[460px] md:h-[560px] overflow-hidden bg-black flex items-center justify-center";

        return isVideo ? (
            <div className={videoFrameClass}>
                <video
                    src={file.fileUrl}
                    title={file.fileName || "Video"}
                    className="w-full h-full object-contain"
                    controls
                    aria-label="Post video"
                />
            </div>
        ) : (
            <div className={imageFrameClass}>
                <img
                    src={file.fileUrl}
                    alt="Post media"
                    className="w-full h-auto object-contain"
                    onError={(e) => {
                        e.currentTarget.src = "https://via.placeholder.com/300";
                    }}
                />
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <Spinner size="lg" color="primary" />
                <span className="ml-2">{t("loadingPosts")}</span>
            </div>
        );
    }

    if (error || !post) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
                <div className="text-red-600 mb-4">{error || t("postNotFound", "Post not found")}</div>
                <button
                    onClick={() => navigate(-1)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                    {t("goBack", "Go Back")}
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            <div className="max-w-3xl mx-auto px-4 py-6">
                <div className="bg-white rounded-lg shadow-sm">
                    {/* Post Header */}
                    <div className="flex items-center justify-between px-5 py-4">
                        <button
                            type="button"
                            onClick={() => navigate(`/profile?profileId=${post.profile.id}`)}
                            className="flex items-start gap-3 text-left rounded-md outline-none focus:outline-none focus:ring-0"
                            aria-label={`${t("viewProfile", "View profile")} ${post.profile.firstName} ${post.profile.lastName}`}
                        >
                            <div className="relative w-12 h-12">
                                <img
                                    src={post.profile.profileImage || "https://via.placeholder.com/48"}
                                    alt={`${post.profile.firstName} ${post.profile.lastName}'s profile`}
                                    className="w-12 h-12 rounded-full object-cover"
                                />
                                {post.profile.verified && (
                                    <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-white rounded-full overflow-hidden" aria-label="Verified user">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="w-full h-full">
                                            <polygon fill="#42a5f5" points="29.62,3 33.053,8.308 39.367,8.624 39.686,14.937 44.997,18.367 42.116,23.995 45,29.62 39.692,33.053 39.376,39.367 33.063,39.686 29.633,44.997 24.005,42.116 18.38,45 14.947,39.692 8.633,39.376 8.314,33.063 3.003,29.633 5.884,24.005 3,18.38 8.308,14.947 8.624,8.633 14.937,8.314 18.367,3.003 23.995,5.884" />
                                            <polygon fill="#fff" points="21.396,31.255 14.899,24.76 17.021,22.639 21.428,27.046 30.996,17.772 33.084,19.926" />
                                        </svg>
                                    </span>
                                )}
                            </div>
                            <div>
                                <h3 className="font-semibold text-start text-sm text-gray-900">
                                    {post.profile.firstName} {post.profile.lastName}
                                </h3>
                                <p className="text-xs text-gray-500">
                                    {formatDateTime(post.createdAt)}{" "}
                                    •{" "}

                                </p>
                            </div>
                        </button>
                    </div>

                    <div className="px-5 pb-3">
                        <h4 className="font-semibold text-base text-start text-gray-900 mb-2 break-words">{post.title}</h4>
                        {post.content && (
                            <p className="text-gray-700 text-sm text-start leading-relaxed whitespace-pre-wrap break-words">
                                {post.content}
                            </p>
                        )}
                    </div>

                    {post.file && renderMedia(post.file)}
                </div>
            </div>
        </div>
    );
};

export default Post;
